import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { inputs, references, whyDiffer } from '../../data/teaser';
import { chapterFor } from '../../data/journey';
import { embed, irreversible, proactive, snak2References, windowOfOpportunity } from '../../data/snak2';
import { fitToClinic, pilot, predict, principles, snak3References } from '../../data/snak3';
import { PhoneFrame } from '../PhoneFrame';
import { EntryScreen } from '../screens/EntryScreen';
import { PromiseScreen } from '../screens/PromiseScreen';
import { WhyDifferScreen } from '../screens/WhyDifferScreen';
import { InputsScreen } from '../screens/InputsScreen';
import { PhenotypesScreen } from '../screens/PhenotypesScreen';
import { BridgeScreen } from '../screens/BridgeScreen';
import { WindowScreen } from '../screens/snak2/WindowScreen';
import { IrreversibilityScreen } from '../screens/snak2/IrreversibilityScreen';
import { ProactiveScreen } from '../screens/snak2/ProactiveScreen';
import { EmbedScreen } from '../screens/snak2/EmbedScreen';
import { PreventScreen } from '../screens/snak2/PreventScreen';
import { PrinciplesScreen } from '../screens/snak3/PrinciplesScreen';
import { FitToClinicScreen } from '../screens/snak3/FitToClinicScreen';
import { PilotScreen } from '../screens/snak3/PilotScreen';
import { PredictScreen } from '../screens/snak3/PredictScreen';
import { CoCreateScreen } from '../screens/snak3/CoCreateScreen';
const EASE = [0.23, 1, 0.32, 1] as const;
type Snak = 1 | 2 | 3;
type Mode = 'menu' | 'campaign' | 'presentation';
interface ScreenDef {
  id: string;
  snak: Snak;
  nextLabel?: string;
  footnote?: string;
}
const ALL_SCREENS: ScreenDef[] = [{
  id: 'promise',
  snak: 1,
  nextLabel: 'Explore the evidence',
  footnote: references.intro
}, {
  id: 'whyDiffer',
  snak: 1,
  nextLabel: whyDiffer.cta,
  footnote: references.midena
}, {
  id: 'inputs',
  snak: 1,
  nextLabel: inputs.cta,
  footnote: references.munk
}, {
  id: 'phenotypes',
  snak: 1,
  footnote: references.midena
}, {
  id: 'bridge',
  snak: 1
}, {
  id: 's2-window',
  snak: 2,
  nextLabel: windowOfOpportunity.cta,
  footnote: snak2References.window
}, {
  id: 's2-irreversible',
  snak: 2,
  nextLabel: irreversible.cta,
  footnote: snak2References.irreversible
}, {
  id: 's2-proactive',
  snak: 2,
  nextLabel: proactive.cta,
  footnote: snak2References.proactive
}, {
  id: 's2-embed',
  snak: 2,
  nextLabel: embed.cta,
  footnote: snak2References.embed
}, {
  id: 's2-prevent',
  snak: 2,
  footnote: snak2References.prevent
}, {
  id: 's3-principles',
  snak: 3,
  nextLabel: principles.cta,
  footnote: snak3References.principles
}, {
  id: 's3-fit',
  snak: 3,
  nextLabel: fitToClinic.cta,
  footnote: snak3References.fitToClinic
}, {
  id: 's3-pilot',
  snak: 3,
  nextLabel: pilot.cta,
  footnote: snak3References.pilot
}, {
  id: 's3-predict',
  snak: 3,
  nextLabel: predict.cta,
  footnote: snak3References.predict
}, {
  id: 's3-cocreate',
  snak: 3,
  footnote: snak3References.coCreate
}];
interface SnakExperienceProps {
  onOpenStrategy: () => void;
  onOpenJourney: () => void;
  onOpenStoryboard: () => void;
}

/** The live Snak: entry menu, one instalment, or all 15 screens in sequence. */
export function SnakExperience({
  onOpenStrategy,
  onOpenJourney,
  onOpenStoryboard
}: SnakExperienceProps) {
  const [mode, setMode] = useState<Mode>('menu');
  const [activeSnak, setActiveSnak] = useState<Snak>(1);
  const [step, setStep] = useState(0);
  const goHome = () => {
    setMode('menu');
    setStep(0);
  };
  if (mode === 'menu') {
    return <PhoneFrame embedded step={0} total={0} chapter="Entry" onOpenStrategy={onOpenStrategy} onOpenJourney={onOpenJourney} data-id="element-340" data-mp="14zx96mzca57u">
        <EntryScreen onStartCampaign={snak => {
        setActiveSnak(snak);
        setMode('campaign');
        setStep(0);
      }} onStartPresentation={() => {
        setMode('presentation');
        setStep(0);
      }} onOpenJourney={onOpenJourney} onOpenStoryboard={onOpenStoryboard} data-id="element-341" data-mp="19t9lbxmu0jjp" />
      </PhoneFrame>;
  }
  const screens = mode === 'presentation' ? ALL_SCREENS : ALL_SCREENS.filter(s => s.snak === activeSnak);
  const screen = screens[Math.min(step, screens.length - 1)];
  const chapter = chapterFor(screen.snak);
  const isLast = step === screens.length - 1;
  const nextScreen = screens[step + 1];
  const crossesChapter = nextScreen && nextScreen.snak !== screen.snak;
  const next = () => setStep(s => Math.min(s + 1, screens.length - 1));
  const back = () => setStep(s => Math.max(s - 1, 0));
  const restart = () => setStep(0);
  return <PhoneFrame embedded step={step} total={screens.length} chapter={chapter.label} brand={chapter.brand} onHome={goHome} onOpenStrategy={onOpenStrategy} onOpenJourney={onOpenJourney} onBack={step > 0 ? back : undefined} onNext={isLast ? undefined : next} nextLabel={crossesChapter ? `Continue to ${chapterFor(nextScreen.snak).label}` : screen.nextLabel} footnote={screen.footnote} data-id="element-342" data-mp="113jc1ci43h0b">
      <AnimatePresence mode="wait" data-id="element-343" data-mp="2f579lquleqpo">
        <motion.div key={screen.id} initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.22,
        ease: EASE
      }} className="flex flex-1 flex-col" data-id="element-344" data-mp="2k27hm7b063jf">
          {screen.id === 'promise' && <PromiseScreen data-id="element-345" data-mp="2xt2lf56n9ug5" />}
          {screen.id === 'whyDiffer' && <WhyDifferScreen data-id="element-346" data-mp="2xvor0z9z5zvq" />}
          {screen.id === 'inputs' && <InputsScreen onAdvance={next} data-id="element-347" data-mp="32r42jkkifjoz" />}
          {screen.id === 'phenotypes' && <PhenotypesScreen data-id="element-348" data-mp="2p1k6exlfpkon" />}
          {screen.id === 'bridge' && <BridgeScreen onRestart={restart} data-id="element-349" data-mp="2oyxxoumt4z4u" />}
          {screen.id === 's2-window' && <WindowScreen data-id="element-350" data-mp="2t12rbtd99vrh" />}
          {screen.id === 's2-irreversible' && <IrreversibilityScreen data-id="element-351" data-mp="3groouqpmvxn8" />}
          {screen.id === 's2-proactive' && <ProactiveScreen data-id="element-352" data-mp="3gqdlgs03orwn" />}
          {screen.id === 's2-embed' && <EmbedScreen data-id="element-353" data-mp="3ljq32i6d3yfw" />}
          {screen.id === 's2-prevent' && <PreventScreen onRestart={mode === 'campaign' ? goHome : undefined} data-id="element-354" data-mp="3vena23xwdvti" />}
          {screen.id === 's3-principles' && <PrinciplesScreen data-id="element-355" data-mp="3rd21uvwb65pn" />}
          {screen.id === 's3-fit' && <FitToClinicScreen data-id="element-356" data-mp="3rbh7b69zumrs" />}
          {screen.id === 's3-pilot' && <PilotScreen data-id="element-357" data-mp="3mcxjt1fr52xb" />}
          {screen.id === 's3-predict' && <PredictScreen data-id="element-358" data-mp="3hfe6q4usiw6y" />}
          {screen.id === 's3-cocreate' && <CoCreateScreen onRestart={goHome} data-id="element-359" data-mp="3hew2om1vzdoh" />}
        </motion.div>
      </AnimatePresence>
    </PhoneFrame>;
}